// SessionMetricsChart: live CPU + memory usage for a single env-host
// session. The session-detail LiveView subscribes to
// `Operation.Topics.env_session(session_id)` and `push_event`s every
// `EnvSessionSample` frame as `sessionMetricsSample`. Initial samples
// come from `Operation.EnvHost.SessionMetricsBuffer.samples/1` baked
// into `data-initial-samples` at render time.
//
// Sibling of RunnerMetricsChart (per-runner aggregated rollup) and
// JobMetricsChart (per-job cgroup samples) — same geometry, same canvas
// pipeline, same gradient-stops-on-path renderer. The only differences
// are the event names and a `session_id` filter so a sample for a
// neighbouring session does not paint into this chart.
//
// Why not the older `<svg>` + `barGeom` rect-per-sample shape: master's
// chart refactor (commit `bbb7e96cdc`) replaced rectangles with a
// canvas path lit by a per-sample horizontal gradient, deleted
// `barGeom` along the way, and centralised the redraw loop in
// `redrawMetrics`. Keeping this hook on rects left the env-host chart
// importing a symbol that no longer existed; the whole frontend bundle
// failed to compile in `nix flake check`. This rewrite mirrors
// `runner-metrics-chart` so a future fix to the renderer lands in all
// three charts at once.

import type { LiveViewHook, CiPushEventWire } from "../types";
import {
  parseRgbTriple,
  noteMetricsSampleForScroll,
  resetMetricsScroll,
  ringPush,
  RING_CAP,
  updateMetricsScrollProgress,
  type ChartPoint,
  type MetricsSample,
  type Rgb,
} from "./job-metrics-chart";
import { cssVar } from "./theme";
import { buildGradientStops } from "./metrics-axes";
import { redrawMetrics } from "./metrics-render";
import {
  DataAttr,
  readPositiveNumberData,
  requireHookCanvas,
} from "./hook-dom-contract";

// Wire shape from `Operation.EnvHost.SessionMetricsBuffer` — snake_case
// keys to match the paradox-emitted JSON. Mirrors the
// `RunnerWireSample` projection pattern in `runner-metrics-chart.ts`.
interface SessionWireSample {
  cpu_us: number;
  mem_bytes: number;
  ts_ms: number;
}

interface SessionMetricsPayload extends SessionWireSample {
  session_id: string;
}

const projectWireSample = (p: SessionWireSample): MetricsSample => ({
  cpuUsageUs: p.cpu_us,
  memoryBytes: p.mem_bytes,
  tsMs: p.ts_ms,
});

interface SessionMetricsChartHook extends LiveViewHook<CiPushEventWire> {
  _sessionId: string;
  _samples: MetricsSample[];
  _chart: ChartPoint[];
  _lastChart?: ChartPoint[] | undefined;
  _lastCpuMax?: number | undefined;
  _lastMemMax?: number | undefined;
  _cores: number;
  _ramBytes: number;
  _ramps?: { cpu: Rgb[]; mem: Rgb[] } | undefined;
  _canvas?: HTMLCanvasElement | undefined;
  _ctx?: CanvasRenderingContext2D | undefined;
  _w: number;
  _h: number;
  _rafId?: number | undefined;
  _ro?: ResizeObserver | undefined;
  _lastSampleTsMs?: number | undefined;
  _scrollStartedAtMs?: number | undefined;
  _scrollDurationMs?: number | undefined;
  _scrollStartOffsetSlots?: number | undefined;
  _scrollOffsetSlots?: number | undefined;
  redraw(this: SessionMetricsChartHook): void;
  scheduleRedraw(this: SessionMetricsChartHook): void;
}

// Read the `data-initial-samples` attr at mount. The server bakes the
// per-session ring buffer (last 60s @ 10Hz) so the chart rehydrates
// synchronously when LV re-mounts (e.g. nav back into session detail).
//
// Missing/empty data → []. Malformed JSON → throws to the console
// rather than silently rendering an empty chart. Initial samples
// either come from a Jason.encode! call in the LV (always valid) or
// they don't come at all; a parse failure means the LV emitted a
// corrupted blob and we want the diagnostic, not a fake-clean chart.
const parseInitialSamples = (el: HTMLElement): MetricsSample[] => {
  const raw = el.dataset["initialSamples"];
  if (!raw || raw === "[]") return [];
  const parsed: unknown = JSON.parse(raw);
  if (!Array.isArray(parsed)) {
    throw new Error(
      `SessionMetricsChart: data-initial-samples is not a JSON array (got ${typeof parsed})`,
    );
  }
  return (parsed as SessionWireSample[]).map(projectWireSample);
};

export const buildRamps = (): { cpu: Rgb[]; mem: Rgb[] } => {
  const stops = buildGradientStops({
    info: parseRgbTriple(cssVar("--c-info")),
    warning: parseRgbTriple(cssVar("--c-warning")),
    danger: parseRgbTriple(cssVar("--c-danger")),
  });
  return { cpu: [...stops.cpu], mem: [...stops.mem] };
};

export const SessionMetricsChart = {
  mounted(this: SessionMetricsChartHook) {
    const sid = this.el.dataset["sessionId"];
    this._sessionId = sid ?? "";
    this._samples = parseInitialSamples(this.el);
    this._chart = [];
    this._cores = readPositiveNumberData(
      this.el,
      "sessionMetricsChart",
      DataAttr.cores,
    );
    this._ramBytes = readPositiveNumberData(
      this.el,
      "sessionMetricsChart",
      DataAttr.ram_bytes,
    );
    this._ramps = buildRamps();
    this._rafId = undefined;
    resetMetricsScroll(this);

    if (this._cores === 0 || this._ramBytes === 0) {
      // Without both denominators the chart can't normalise; surface
      // once at mount so an operator can spot a missing host-caps
      // wiring instead of staring at a flat chart.
      console.warn(
        "SessionMetricsChart: missing or invalid host denominators",
        {
          cores: this.el.dataset["cores"],
          ramBytes: this.el.dataset["ramBytes"],
        },
      );
    }

    const rect = this.el.getBoundingClientRect();
    this._w = rect.width || 320;
    this._h = rect.height || 80;

    const canvas = requireHookCanvas(this.el, "sessionMetricsChart");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    this._canvas = canvas;
    this._ctx = ctx;

    this._ro = new ResizeObserver(() => {
      const r = this.el.getBoundingClientRect();
      this._w = r.width || this._w;
      this._h = r.height || this._h;
      this._lastChart = undefined;
      this.scheduleRedraw();
    });
    this._ro.observe(this.el);

    // Paint rehydrated samples on next rAF so initialSamples don't
    // wait for the first live frame to land.
    this.scheduleRedraw();

    // Wire form is the camelCase dox-source variant name that
    // `Dox.Ui.CiPushEvent.to_json(:session_metrics_sample)` returns —
    // matches the `"applyTheme"`, `"jobMetricsSample"`, etc. shape used
    // elsewhere. The illuminated snake-case-colon form ("session_metrics:sample")
    // is the receive-side handle_event wire string, NOT the push_event one.
    this.handleEvent("sessionMetricsSample", (raw: unknown) => {
      const p = raw as SessionMetricsPayload;
      // Multiple session-detail panels can share a LiveView mount in
      // theory; filter by `session_id` defensively so a sample for
      // a sibling session doesn't paint into this chart.
      if (p.session_id !== this._sessionId) return;
      const s = projectWireSample(p);
      this._samples = ringPush(this._samples, s, RING_CAP);
      noteMetricsSampleForScroll(this, s.tsMs);
      this.scheduleRedraw();
    });
  },

  scheduleRedraw(this: SessionMetricsChartHook) {
    if (this._rafId != null) return;
    this._rafId = requestAnimationFrame((nowMs) => {
      this._rafId = undefined;
      const keepAnimating = updateMetricsScrollProgress(this, nowMs);
      this.redraw();
      if (keepAnimating) this.scheduleRedraw();
    });
  },

  redraw(this: SessionMetricsChartHook) {
    redrawMetrics(this, {
      idBase: this.el.id || `session-metrics-${this._sessionId}`,
      rampsFallback: buildRamps,
    });
  },

  destroyed(this: SessionMetricsChartHook) {
    if (this._rafId != null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = undefined;
    }
    this._ro?.disconnect();
    this._ro = undefined;
    this._canvas = undefined;
    this._ctx = undefined;
    this._ramps = undefined;
    this._samples = [];
    this._chart = [];
    this._lastChart = undefined;
    this._lastCpuMax = undefined;
    this._lastMemMax = undefined;
    resetMetricsScroll(this);
  },
};
